import type { Row, RowData } from "@tanstack/react-table";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";

dayjs.extend(relativeTime);
dayjs.extend(utc);

const ABSOLUTE_FORMAT = "YYYY-MM-DD";
const HOURS_IN_DAY = 24;
const MINUTES_IN_HOUR = 60;

function isWithInAWeek(date: dayjs.Dayjs): boolean {
  const numberOfDays = 7;
  const weekOld = dayjs().subtract(numberOfDays, "days").startOf("day");

  return date.isAfter(weekOld);
}

const parseDate = (value: string): dayjs.Dayjs | null => {
  if (!value || value === "-") {
    return null;
  }

  const date = dayjs(value);

  return date.isValid() ? date : null;
};

const formatDate = (value: string): string =>
  value && value !== "-" ? dayjs(value).format("YYYY-MM-DD") : "-";

const formatDateTime = (value: string): string =>
  value && value !== "-" ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-";

const formatRelative = (value: string): string =>
  value && value !== "-" ? dayjs(value).fromNow() : "-";

const formatApiDate = (
  value: Date | dayjs.Dayjs | string | null | undefined,
): string => {
  const parsed = dayjs(value ?? "");

  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const formatBillingDate = (value: string | null): string =>
  value === null || value === "" ? "-" : formatApiDate(dayjs.utc(value));

const formatAbsoluteDate = (value: string): string => {
  const date = parseDate(value);

  return date === null ? "-" : date.format(ABSOLUTE_FORMAT);
};

const formatHybridDate = (value: string): string => {
  const date = parseDate(value);
  if (date === null) {
    return "-";
  }

  const minutes = dayjs().diff(date, "minute");
  if (minutes < MINUTES_IN_HOUR) {
    return `${Math.max(minutes, 1)} min ago`;
  }

  const hours = dayjs().diff(date, "hour");
  if (hours < HOURS_IN_DAY) {
    return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  }

  return date.format(ABSOLUTE_FORMAT);
};

const getDatePlusDeltaDays = (date: string, days: number): string =>
  dayjs(date).add(days, "days").format("YYYY-MM-DD hh:mm:ss A");

const getRemainingDays = (value: string): number =>
  dayjs(value).diff(dayjs(), "days");

const filterDate = <TData extends RowData>(
  row: Row<TData>,
  columnId: string,
  filterValue: [number | undefined, number | undefined],
): boolean => {
  const currentDate = dayjs(row.getValue<string>(columnId)).valueOf();
  const isHigher =
    filterValue[0] === undefined ? true : currentDate >= filterValue[0];
  const isLower =
    filterValue[1] === undefined ? true : currentDate <= filterValue[1];

  return isHigher && isLower;
};

export {
  formatAbsoluteDate,
  formatApiDate,
  formatBillingDate,
  formatDate,
  formatDateTime,
  formatHybridDate,
  formatRelative,
  getDatePlusDeltaDays,
  getRemainingDays,
  isWithInAWeek,
  filterDate,
};
